import { Suspense, ComponentType, ReactElement } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

interface AdminRouteLoaderProps {
  children: ReactElement;
  fallback?: ReactElement;
}

const DefaultLoadingFallback = () => (
  <div className="space-y-6 p-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
    </div>
    <div className="space-y-3">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  </div>
);

/**
 * Lazy loading wrapper for admin routes
 * Provides consistent loading states and error boundaries
 */
export function AdminRouteLoader({ children, fallback }: AdminRouteLoaderProps) {
  return (
    <Suspense fallback={fallback || <DefaultLoadingFallback />}>
      {children}
    </Suspense>
  );
}

/**
 * HOC to wrap lazy-loaded admin components
 */
export function withAdminRouteLoader<P extends object>(
  Component: ComponentType<P>,
  fallback?: ReactElement
) {
  return (props: P) => (
    <AdminRouteLoader fallback={fallback}>
      <Component {...props} />
    </AdminRouteLoader>
  );
}
